import { useState } from 'react';
import { Link } from 'react-router-dom';
import { notifications as initialNotifications } from '@/mocks/dashboard';
import type { Notification } from '@/mocks/dashboard';
import SAR from '@/components/feature/SAR';

type FilterType = 'all' | 'unread' | 'donation' | 'milestone' | 'review' | 'expiry' | 'goal' | 'tip';

const typeConfig: Record<string, { icon: string; color: string; bg: string; label: string }> = {
  donation: {
    icon: 'ri-heart-fill',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    label: 'تبرع جديد',
  },
  milestone: {
    icon: 'ri-trophy-fill',
    color: 'text-amber-500',
    bg: 'bg-amber-50',
    label: 'إنجاز',
  },
  review: {
    icon: 'ri-shield-check-fill',
    color: 'text-sky-500',
    bg: 'bg-sky-50',
    label: 'مراجعة',
  },
  expiry: {
    icon: 'ri-timer-flash-fill',
    color: 'text-red-500',
    bg: 'bg-red-50',
    label: 'انتهاء وشيك',
  },
  goal: {
    icon: 'ri-flag-fill',
    color: 'text-violet-500',
    bg: 'bg-violet-50',
    label: 'هدف',
  },
  tip: {
    icon: 'ri-lightbulb-fill',
    color: 'text-orange-400',
    bg: 'bg-orange-50',
    label: 'نصيحة',
  },
};

const filters: { key: FilterType; label: string }[] = [
  { key: 'all', label: 'الكل' },
  { key: 'unread', label: 'غير مقروء' },
  { key: 'donation', label: 'تبرعات' },
  { key: 'milestone', label: 'إنجازات' },
  { key: 'review', label: 'مراجعة' },
  { key: 'expiry', label: 'انتهاء وشيك' },
];

const NotificationsPage = () => {
  const [notifList, setNotifList] = useState<Notification[]>(initialNotifications);
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');

  const unreadCount = notifList.filter((n) => !n.isRead).length;

  const filtered = notifList.filter((n) => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'unread') return !n.isRead;
    return n.type === activeFilter;
  });

  // Group by date
  const grouped = filtered.reduce<Record<string, Notification[]>>((acc, n) => {
    const key = n.date;
    if (!acc[key]) acc[key] = [];
    acc[key].push(n);
    return acc;
  }, {});

  const dateOrder = ['اليوم', 'أمس', 'الأسبوع الماضي', 'هذا الشهر'];
  const sortedDates = Object.keys(grouped).sort(
    (a, b) => dateOrder.indexOf(a) - dateOrder.indexOf(b)
  );

  const markAllRead = () => {
    setNotifList((prev) => prev.map((n) => ({ ...n, isRead: true })));
  };

  const markOneRead = (id: string) => {
    setNotifList((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
    );
  };

  const deleteNotif = (id: string) => {
    setNotifList((prev) => prev.filter((n) => n.id !== id));
  };

  return (
    <div className="min-h-screen bg-stone-50" dir="rtl">

      <div className="max-w-2xl mx-auto px-4 md:px-6 pt-24 pb-16">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link
              to="/dashboard"
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-stone-200 text-stone-500 hover:bg-stone-50 cursor-pointer transition-colors"
            >
              <i className="ri-arrow-right-line text-base"></i>
            </Link>
            <div>
              <h1 className="text-xl font-black text-stone-800">الإشعارات</h1>
              {unreadCount > 0 && (
                <p className="text-xs text-stone-400">{unreadCount} إشعار غير مقروء</p>
              )}
            </div>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 cursor-pointer flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-emerald-50 transition-colors"
            >
              <i className="ri-check-double-line text-sm"></i>
              تحديد الكل كمقروء
            </button>
          )}
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-1.5 mb-6">
          {filters.map((f) => {
            const count = f.key === 'unread'
              ? notifList.filter((n) => !n.isRead).length
              : f.key === 'all'
              ? notifList.length
              : notifList.filter((n) => n.type === f.key).length;

            return (
              <button
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all border ${
                  activeFilter === f.key
                    ? 'bg-stone-800 text-white border-stone-800'
                    : 'bg-white text-stone-500 border-stone-200 hover:border-stone-300'
                }`}
              >
                {f.label}
                {count > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                    activeFilter === f.key ? 'bg-white/20 text-white' : 'bg-stone-100 text-stone-500'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Empty State */}
        {filtered.length === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-2xl mx-auto mb-4">
              <i className="ri-notification-off-line text-2xl text-stone-400"></i>
            </div>
            <p className="text-stone-500 font-semibold mb-1">لا توجد إشعارات</p>
            <p className="text-stone-400 text-sm">ستظهر هنا تحديثات حملاتك</p>
          </div>
        )}

        {/* Grouped Notifications */}
        <div className="space-y-6">
          {sortedDates.map((date) => (
            <div key={date}>
              {/* Date Label */}
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs font-bold text-stone-400 whitespace-nowrap">{date}</span>
                <div className="flex-1 h-px bg-stone-200"></div>
              </div>

              {/* Notifications in this group */}
              <div className="space-y-2">
                {grouped[date].map((notif) => {
                  const cfg = typeConfig[notif.type] ?? typeConfig.tip;
                  return (
                    <div
                      key={notif.id}
                      className={`relative bg-white rounded-2xl border transition-all ${
                        !notif.isRead
                          ? 'border-emerald-200 shadow-sm'
                          : 'border-stone-100'
                      }`}
                    >
                      {/* Unread dot */}
                      {!notif.isRead && (
                        <div className="absolute top-4 left-4 w-2 h-2 bg-emerald-500 rounded-full"></div>
                      )}

                      <div className="flex items-start gap-3 p-4">
                        {/* Icon */}
                        <div className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${cfg.bg}`}>
                          <i className={`${cfg.icon} text-base ${cfg.color}`}></i>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-0.5">
                            <p className={`text-sm font-bold leading-snug ${!notif.isRead ? 'text-stone-800' : 'text-stone-600'}`}>
                              {notif.message}
                            </p>
                            {notif.amount && (
                              <span className="text-sm font-black text-emerald-600 shrink-0">
                                +<SAR amount={notif.amount} iconSize={12} />
                              </span>
                            )}
                          </div>

                          {notif.detail && (
                            <p className="text-xs text-stone-400 leading-relaxed mb-2">{notif.detail}</p>
                          )}

                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.bg} ${cfg.color}`}>
                                {cfg.label}
                              </span>
                              <span className="text-xs text-stone-300">{notif.time}</span>
                            </div>

                            <div className="flex items-center gap-1">
                              {notif.campaignId && (
                                <Link
                                  to={`/campaign/${notif.campaignId}`}
                                  onClick={() => markOneRead(notif.id)}
                                  className="text-xs text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer px-2 py-1 rounded-lg hover:bg-emerald-50 transition-colors flex items-center gap-1"
                                >
                                  <i className="ri-external-link-line text-xs"></i>
                                  عرض الحملة
                                </Link>
                              )}
                              {!notif.isRead && (
                                <button
                                  onClick={() => markOneRead(notif.id)}
                                  className="text-xs text-stone-400 hover:text-stone-600 cursor-pointer px-2 py-1 rounded-lg hover:bg-stone-50 transition-colors"
                                  title="تحديد كمقروء"
                                >
                                  <i className="ri-check-line text-sm"></i>
                                </button>
                              )}
                              <button
                                onClick={() => deleteNotif(notif.id)}
                                className="text-xs text-stone-300 hover:text-red-400 cursor-pointer px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
                                title="حذف الإشعار"
                              >
                                <i className="ri-delete-bin-line text-sm"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Footer hint */}
        {filtered.length > 0 && (
          <p className="text-center text-xs text-stone-300 mt-10">
            يتم الاحتفاظ بالإشعارات لمدة 30 يوماً
          </p>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
